import { useState } from "react";
import { toast } from "sonner";
import { Check, Gift, Heart, Sparkles, Star, MessageCircle } from "lucide-react";
import { Reveal } from "../Reveal";
import { Overline } from "../SectionHeading";
import { submitWhatsAppLead } from "../../services/api";

const PERKS = [
  { icon: Gift, label: "Exclusive Offers" },
  { icon: Sparkles, label: "New Arrivals" },
  { icon: Heart, label: "Style Inspiration" },
  { icon: Star, label: "Bracelet Care Tips" },
];

// Newsletter — first to know about new bracelet drops.
export const Newsletter = () => {
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const digits = phone.replace(/\D/g, "").replace(/^91/, "");
    if (!/^[6-9]\d{9}$/.test(digits)) {
      setError("Please enter a valid 10-digit Indian WhatsApp number.");
      return;
    }
    if (!consent) {
      setError("Please tick the box to receive WhatsApp updates.");
      return;
    }
    setBusy(true);
    try {
      await submitWhatsAppLead({ whatsapp: digits, consent });
      setDone(true);
      toast.success("You're on the list.", { description: "New bracelet drops, straight to your WhatsApp." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "We couldn't save that number. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border-t border-brand-sand bg-brand-champagne py-20 sm:py-24" data-testid="newsletter-section">
      <div className="mx-auto max-w-2xl px-4 text-center sm:px-6">
        <Reveal>
          <Overline className="justify-center">Stay in the Loop</Overline>
          <h2 className="mt-5 font-serif text-4xl font-medium leading-[1.1] tracking-tight text-brand-charcoal sm:text-5xl text-balance">
            Be the first to know about new bracelet drops
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-brand-stone sm:text-base">
            Small-batch releases, styling notes and private previews — shared gently, only when there is something worth sharing.
          </p>
          {done ? (
            <div className="mx-auto mt-10 flex max-w-md items-center justify-center gap-3 border border-brand-gold/50 bg-brand-surface px-6 py-5" data-testid="newsletter-success">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-gold text-brand-cream">
                <Check className="h-4 w-4" />
              </span>
              <p className="font-serif text-lg text-brand-charcoal">You're on the HESHAURA WhatsApp list.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mx-auto mt-10 max-w-md space-y-3" data-testid="newsletter-form" noValidate>
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="flex flex-1">
                  <span className="flex items-center border border-r-0 border-brand-sand bg-brand-cream px-4 text-sm text-brand-stone">+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^\d\s+-]/g, ""))}
                    placeholder="WhatsApp number"
                    autoComplete="tel-national"
                    data-testid="newsletter-whatsapp-input"
                    className="w-full border border-brand-sand bg-brand-surface px-5 py-4 text-sm text-brand-charcoal outline-none transition-colors placeholder:text-brand-stone/60 focus:border-brand-gold"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="newsletter-submit-button"
                  className="inline-flex items-center justify-center gap-2 bg-brand-charcoal px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-cream transition-colors duration-300 hover:bg-brand-gold disabled:opacity-50"
                >
                  <MessageCircle className="h-4 w-4" />
                  {busy ? "Joining…" : "Join on WhatsApp"}
                </button>
              </div>
              <label className="flex cursor-pointer items-start gap-3 pt-1 text-left text-xs leading-relaxed text-brand-stone">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="newsletter-consent-checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#B09460]"
                />
                <span>I agree to receive HESHAURA offers and updates on WhatsApp.</span>
              </label>
              {error && (
                <p className="text-left text-sm font-medium text-destructive" data-testid="newsletter-error">{error}</p>
              )}
            </form>
          )}
          <div className="mx-auto mt-12 grid max-w-lg grid-cols-2 gap-6 sm:grid-cols-4">
            {PERKS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-2.5">
                <Icon className="h-5 w-5 text-brand-gold" strokeWidth={1.5} />
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-stone">{label}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
